import React, { useState, useEffect, useRef } from 'react';
import tagService from '../services/tagService';
import ideaService from '../services/ideaService';
import { API_URL } from '../config';
import { 
  CloseIcon, BoldIcon, ItalicIcon, LinkIcon, ListIcon, QuoteIcon, 
  CodeIcon, ImageIcon, VideoIcon, MicrophoneIcon, FilmIcon, MusicIcon, 
  HashtagIcon, SpinnerIcon, SearchIcon 
} from './Icons';

const IdeaForm = ({ onClose, onSubmit, initialContent = '', initialTags = [], initialMedia = [], isEditing = false }) => {
  const [content, setContent] = useState(initialContent);
  const [tags, setTags] = useState(initialTags);
  const [tagInput, setTagInput] = useState('');
  const [media, setMedia] = useState(initialMedia.map(m => ({
    id: m.id,
    type: m.mime?.startsWith('image/') ? 'image' : 
          m.mime?.startsWith('video/') ? 'video' : 'audio',
    preview: m.mime?.startsWith('image/') ? (m.url.startsWith('http') ? m.url : `${API_URL}${m.url}`) : null,
    url: m.url,
    mime: m.mime
  })));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const contentRef = useRef(null);
  const tagInputRef = useRef(null);
  const draftSaveTimerRef = useRef(null);
  const tagSearchTimerRef = useRef(null);
  const [contentHeight, setContentHeight] = useState('auto');
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [showMobileTagModal, setShowMobileTagModal] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      // 多重检测确保在 iPhone Safari 中正确识别移动端
      const screenWidth = window.innerWidth <= 768;
      const userAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const touchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const iOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      // 如果是 iOS 设备或者满足其他移动端条件，则认为是移动端
      setIsMobile(iOSDevice || (screenWidth && (userAgent || touchDevice)));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Handle keyboard visibility on mobile
  useEffect(() => {
    if (!isMobile) return;

    let initialViewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    
    const handleViewportChange = () => {
      if (window.visualViewport) {
        const currentHeight = window.visualViewport.height;
        const heightDifference = initialViewportHeight - currentHeight;
        setIsKeyboardVisible(heightDifference > 150); // 键盘高度通常大于150px
      }
    };

    const handleResize = () => {
      const currentHeight = window.innerHeight;
      const heightDifference = initialViewportHeight - currentHeight;
      setIsKeyboardVisible(heightDifference > 150);
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
    } else {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [isMobile]);

  // Auto-resize textarea
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.height = 'auto';
      contentRef.current.style.height = `${contentRef.current.scrollHeight}px`;
      setContentHeight(`${contentRef.current.scrollHeight}px`);
    }
  }, [content]);

  // Load draft from localStorage (only for new ideas, not when editing)
  useEffect(() => {
    if (!isEditing) {
      const savedDraft = localStorage.getItem('ideaDraft');
      if (savedDraft) {
        try {
          const parsedDraft = JSON.parse(savedDraft);
          setContent(parsedDraft.content || '');
          setTags(parsedDraft.tags || []);
          // Media can't be stored in localStorage, only restore text and tags
        } catch (error) {
          console.error('Failed to parse saved draft:', error);
        }
      }
    }
  }, [isEditing]);

  // Auto-save draft (only for new ideas, not when editing)
  useEffect(() => {
    if (!isEditing) {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
      }

      draftSaveTimerRef.current = setTimeout(() => {
        const draft = {
          content,
          tags,
          lastSaved: new Date().toISOString()
        };
        localStorage.setItem('ideaDraft', JSON.stringify(draft));
        setIsSaved(true);
        
        // Reset the "saved" indicator after 2 seconds
        setTimeout(() => setIsSaved(false), 2000);
      }, 1000);

      return () => {
        if (draftSaveTimerRef.current) {
          clearTimeout(draftSaveTimerRef.current);
        }
      };
    }
  }, [content, tags, isEditing]);

  // Search tags with debounce
  useEffect(() => {
    if (tagSearchTimerRef.current) {
      clearTimeout(tagSearchTimerRef.current);
    }

    if (tagInput.trim() && !isComposing) {
      setIsLoadingTags(true);
      tagSearchTimerRef.current = setTimeout(async () => {
        try {
          const results = await tagService.searchTags(tagInput.trim());
          const filteredTags = results
            .filter(tag => tag && tag.attributes) // 确保 tag 和 attributes 存在
            .map(tag => tag.attributes.name)
            .filter(tag => tag && !tags.includes(tag))
            .slice(0, 5);
          setSuggestedTags(filteredTags);
          setShowSuggestions(filteredTags.length > 0);
        } catch (error) {
          console.error('Failed to search tags:', error);
          setSuggestedTags([]);
          setShowSuggestions(false);
        } finally {
          setIsLoadingTags(false);
        }
      }, 300);
    } else {
      setSuggestedTags([]);
      setShowSuggestions(false);
      setIsLoadingTags(false);
    }

    return () => {
      if (tagSearchTimerRef.current) {
        clearTimeout(tagSearchTimerRef.current);
      }
    };
  }, [tagInput, tags, isComposing]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagInputRef.current && !tagInputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle paste upload for images
  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            imageFiles.push(file);
          }
        }
      }

      if (imageFiles.length > 0) {
        // 检查媒体文件数量限制
        if (imageFiles.length + media.length > 4) {
          alert('最多只能上传4个媒体文件');
          return;
        }

        // 复用现有的媒体处理逻辑
        const newMedia = imageFiles.map(file => ({
          id: Date.now() + Math.random().toString(36).substr(2, 9),
          file,
          type: 'image',
          preview: URL.createObjectURL(file)
        }));

        setMedia([...media, ...newMedia]);
        e.preventDefault();
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [media]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!content.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      // 使用新的批量处理API处理标签
      let validTags = [];
      if (tags.length > 0) {
        try {
          const processedTags = await tagService.findOrCreateTags(tags);
          validTags = processedTags.map(tag => ({ id: tag.id }));
          console.log('Processed tags:', processedTags);
        } catch (error) {
          console.error('Failed to process tags:', error);
          // 如果批量处理失败，回退到逐个处理
          const processedTags = await Promise.all(
            tags.map(async (tagName) => {
              try {
                const existingTags = await tagService.findExactTag(tagName);
                if (existingTags && existingTags.length > 0) {
                  return { id: existingTags[0].id };
                }
                const newTag = await tagService.createTag(tagName);
                return newTag && newTag.id ? { id: newTag.id } : null;
              } catch (error) {
                console.error(`Failed to process tag ${tagName}:`, error);
                return null;
              }
            })
          );
          validTags = processedTags.filter(tag => tag !== null);
        }
      }
      
      // 上传媒体文件
      const mediaIds = await Promise.all(
        media.map(async (m) => {
          try {
            if (m.file) {
              // 如果是新上传的文件，需要先上传到媒体库
              const uploadResult = await ideaService.uploadMedia(m.file);
              if (uploadResult && uploadResult.id) {
                return { id: uploadResult.id };
              }
              console.error('Upload result missing ID:', uploadResult);
              return null;
            }
            if (m.id) {
              return { id: m.id };
            }
            return null;
          } catch (error) {
            console.error('Failed to upload media:', error);
            return null;
          }
        })
      ).then(results => results.filter(item => item !== null));

      // 提交数据
      const ideaData = {
        content,
        tags: validTags,
        media: mediaIds
      };

      await onSubmit(ideaData);

      if (!isEditing) {
        // 只有在创建新片语时才清空表单和本地存储
        setContent('');
        setTags([]);
        setMedia([]);
        localStorage.removeItem('ideaDraft');
      }
      
      onClose();
    } catch (error) {
      console.error(`Failed to ${isEditing ? 'update' : 'create'} idea:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddTag = (e) => {
    if (isComposing) return;
    
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addCurrentTag();
    }
  };

  // 添加当前输入的标签
  const addCurrentTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag) && tags.length < 5) {
      setTags([...tags, trimmedTag]);
      setTagInput('');
      setShowSuggestions(false);
    }
  };

  const handleTagInputBlur = () => {
    // Add the tag when the input loses focus if there's content
    if (tagInput.trim() && !isComposing) {
      addCurrentTag();
    }
  };

  const handleSelectSuggestedTag = (tag) => {
    if (!tags.includes(tag) && tags.length < 5) {
      setTags([...tags, tag]);
      setTagInput('');
      setShowSuggestions(false);
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // Handle tag input focus on mobile to prevent keyboard blocking
  const handleTagInputFocus = () => {
    if (isMobile) {
      // 在移动端打开标签输入模态框而不是直接聚焦输入框
      setShowMobileTagModal(true);
      return;
    }
    
    if (tagInputRef.current) {
      // 延迟执行，等待键盘弹出
      setTimeout(() => {
        const tagContainer = tagInputRef.current;
        if (tagContainer) {
          // 使用scrollIntoView确保标签输入框可见
          tagContainer.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest'
          });
          
          // 额外的滚动调整，确保输入框不被键盘遮挡
          setTimeout(() => {
            const rect = tagContainer.getBoundingClientRect();
            const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
            
            // 如果标签输入框仍然被遮挡，进行额外滚动
            if (rect.bottom > viewportHeight - 50) { // 留50px的缓冲区
              const additionalScroll = rect.bottom - viewportHeight + 80; // 额外80px缓冲
              window.scrollBy({
                top: additionalScroll,
                behavior: 'smooth'
              });
            }
          }, 100);
        }
      }, 300); // 等待键盘动画完成
    }
  };

  // Handle mobile tag modal
  const handleMobileTagInputChange = (e) => {
    setTagInput(e.target.value);
  };

  const handleMobileTagAdd = (e) => {
    if (isComposing) return;
    
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addCurrentTag();
    }
  };

  const handleMobileTagModalClose = () => {
    setShowMobileTagModal(false);
    setTagInput('');
    setShowSuggestions(false);
  };

  const handleMobileTagClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 调试日志
    console.log('Mobile tag click triggered, isMobile:', isMobile);
    
    if (isMobile) {
      setShowMobileTagModal(true);
    }
  };

  // 处理触摸事件，确保在 iPhone Safari 中正常工作
  const handleMobileTagTouch = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Mobile tag touch triggered, isMobile:', isMobile);
    
    // iPhone Safari 特殊处理：延迟执行以确保状态更新
    setTimeout(() => {
      if (isMobile) {
        setShowMobileTagModal(true);
      }
    }, 50);
  };

  // 添加专门的 iPhone Safari 处理函数
  const handleMobileTagTouchEnd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Mobile tag touchend triggered, isMobile:', isMobile);
    
    // 确保在 touchend 时也能触发
    if (isMobile && !showMobileTagModal) {
      setShowMobileTagModal(true);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + media.length > 4) {
      alert('最多只能上传4个媒体文件');
      return;
    }

    const newMedia = files.map(file => ({
      id: Date.now() + Math.random().toString(36).substr(2, 9),
      file,
      type: file.type.startsWith('image/') ? 'image' : 
            file.type.startsWith('video/') ? 'video' : 'audio',
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));

    setMedia([...media, ...newMedia]);
  };

  const handleRemoveMedia = (id) => {
    setMedia(media.filter(item => item.id !== id));
  };

  const insertMarkdown = (markdownSyntax) => {
    const textarea = contentRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    
    let newText;
    let newCursorPos;
    
    switch (markdownSyntax) {
      case 'bold':
        if (selectedText) {
          // 直接在选中文本两侧添加**，不添加额外换行
          newText = content.substring(0, start) + `**${selectedText}**` + content.substring(end);
          newCursorPos = end + 4; // After the selection + **
        } else {
          // 插入默认文本时也不添加换行
          newText = content.substring(0, start) + `**粗体文本**` + content.substring(end);
          newCursorPos = start + 4; // Position cursor between **text**
        }
        break;
      case 'italic':
        if (selectedText) {
          newText = content.substring(0, start) + `*${selectedText}*` + content.substring(end);
          newCursorPos = end + 2; // After the selection + *
        } else {
          newText = content.substring(0, start) + `*斜体文本*` + content.substring(end);
          newCursorPos = start + 3; // Position cursor between *text*
        }
        break;
      case 'link':
        if (selectedText) {
          newText = content.substring(0, start) + `[${selectedText}](URL)` + content.substring(end);
          newCursorPos = end + selectedText.length + 3; // Position cursor at URL
        } else {
          newText = content.substring(0, start) + `[链接文本](URL)` + content.substring(end);
          newCursorPos = start + 5; // Position cursor between [text]
        }
        break;
      case 'list':
        // Check if we're at the beginning of a line
        const beforeStart = content.substring(0, start);
        const isStartOfLine = beforeStart.length === 0 || beforeStart.endsWith('\n');
        
        if (isStartOfLine) {
          newText = content.substring(0, start) + `- ${selectedText || '列表项'}` + content.substring(end);
        } else {
          newText = content.substring(0, start) + `\n- ${selectedText || '列表项'}` + content.substring(end);
        }
        newCursorPos = start + (isStartOfLine ? 2 : 3) + (selectedText ? selectedText.length : 3);
        break;
      case 'quote':
        // Check if we're at the beginning of a line
        const beforeQuote = content.substring(0, start);
        const isQuoteStartOfLine = beforeQuote.length === 0 || beforeQuote.endsWith('\n');
        
        if (isQuoteStartOfLine) {
          newText = content.substring(0, start) + `> ${selectedText || '引用文本'}` + content.substring(end);
        } else {
          newText = content.substring(0, start) + `\n> ${selectedText || '引用文本'}` + content.substring(end);
        }
        newCursorPos = start + (isQuoteStartOfLine ? 2 : 3) + (selectedText ? selectedText.length : 4);
        break;
      case 'code':
        if (selectedText) {
          newText = content.substring(0, start) + `\`${selectedText}\`` + content.substring(end);
          newCursorPos = end + 2; // After the selection + `
        } else {
          newText = content.substring(0, start) + `\`代码\`` + content.substring(end);
          newCursorPos = start + 2; // Position cursor between `code`
        }
        break;
      default:
        newText = content;
        newCursorPos = end;
    }
    
    setContent(newText);
    
    // Focus back to textarea and set cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Render markdown preview with improved handling of lists, quotes and line breaks
  const renderMarkdown = () => {
    // Process content for preview
    let html = content
      // Handle bold text - non-greedy match to prevent overlapping
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Handle italic text - non-greedy match
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Handle links
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2">$1</a>')
      // Handle lists - match at start of line with proper spacing
      .replace(/^- (.*)/gm, '<li>$1</li>')
      // Handle quotes - match at start of line with proper spacing
      .replace(/^> (.*)/gm, '<blockquote>$1</blockquote>')
      // Handle inline code
      .replace(/`(.*?)`/g, '<code>$1</code>')
      // 先处理单个换行符，将其转换为<br>
      .replace(/([^\n])\n([^\n])/g, '$1<br>$2')
      // 然后处理段落（两个或更多换行符）
      .split(/\n\n+/).map(paragraph => {
        // 检查段落是否是列表项
        if (paragraph.startsWith('<li>')) {
          return `<ul>${paragraph}</ul>`;
        } else {
          // 确保段落内的<br>标签被保留
          return `<p>${paragraph}</p>`;
        }
      }).join('');
    
    return { __html: html };
  };

  return (
    <div className={`editor-modal ${isMobile && isKeyboardVisible ? 'keyboard-visible' : ''}`}>
      <div className="editor-header">
        <h2>{isEditing ? '编辑片语' : '发布片语'}</h2>
        <div className="editor-actions">
          {isSaved && <span className="draft-saved">草稿已保存</span>}
          <button 
            type="button" 
            className="preview-button" 
            onClick={() => setIsPreview(!isPreview)}
          >
            {isPreview ? '编辑' : '预览'}
          </button>
          <button 
            type="button" 
            className="publish-button"
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
          >
            {isSubmitting ? '处理中...' : isEditing ? '更新' : '发布'}
          </button>
          <button 
            type="button" 
            className="modal-close-button" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            aria-label="关闭"
          >
            <CloseIcon size={16} />
          </button>
        </div>
      </div>

      <div className="editor-toolbar">
        <button type="button" onClick={() => insertMarkdown('bold')} title="粗体">
          <BoldIcon size={16} />
        </button>
        <button type="button" onClick={() => insertMarkdown('italic')} title="斜体">
          <ItalicIcon size={16} />
        </button>
        <button type="button" onClick={() => insertMarkdown('link')} title="链接">
          <LinkIcon size={16} />
        </button>
        <button type="button" onClick={() => insertMarkdown('list')} title="列表">
          <ListIcon size={16} />
        </button>
        <button type="button" onClick={() => insertMarkdown('quote')} title="引用">
          <QuoteIcon size={16} />
        </button>
        <button type="button" onClick={() => insertMarkdown('code')} title="代码">
          <CodeIcon size={16} />
        </button>
        <div className="toolbar-divider"></div>
        <label className="toolbar-button" title="图片">
          <ImageIcon size={16} />
          <input 
            type="file" 
            accept="image/*" 
            onChange={handleFileUpload} 
            multiple 
            style={{ display: 'none' }} 
          />
        </label>
        <label className="toolbar-button" title="视频">
          <VideoIcon size={16} />
          <input 
            type="file" 
            accept="video/*" 
            onChange={handleFileUpload} 
            style={{ display: 'none' }} 
          />
        </label>
        <label className="toolbar-button" title="音频">
          <MicrophoneIcon size={16} />
          <input 
            type="file" 
            accept="audio/*" 
            onChange={handleFileUpload} 
            style={{ display: 'none' }} 
          />
        </label>
      </div>

      <div className="editor-content">
        {isPreview ? (
          <div className="markdown-preview" dangerouslySetInnerHTML={renderMarkdown()}></div>
        ) : (
          <textarea
            ref={contentRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="开始写作..."
            className="content-textarea"
            style={{ height: contentHeight }}
          />
        )}
      </div>

      {/* Media preview */}
      {media.length > 0 && (
        <div className="media-previews">
          {media.map(item => (
            <div key={item.id} className="media-preview-item">
              {item.type === 'image' ? (
                <img src={item.preview || (item.url?.startsWith('http') ? item.url : `${API_URL}${item.url}`)} alt="Preview" />
              ) : item.type === 'video' ? (
                <div className="video-preview">
                  <FilmIcon size={20} />
                  <span>{item.file ? item.file.name : '视频文件'}</span>
                </div>
              ) : (
                <div className="audio-preview">
                  <MusicIcon size={20} />
                  <span>{item.file ? item.file.name : '音频文件'}</span>
                </div>
              )}
              <button 
                type="button" 
                className="remove-media" 
                onClick={() => handleRemoveMedia(item.id)}
              >
                <CloseIcon size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Tags input */}
      <div className="tags-input-container" ref={tagInputRef}>
        <div className="tags-list">
          <HashtagIcon size={16} />
          {tags.map(tag => (
            <span key={tag} className="tag">
              {tag}
              <button type="button" onClick={() => handleRemoveTag(tag)}>
                <CloseIcon size={12} />
              </button>
            </span>
          ))}
          {tags.length < 5 && (
            <div className="tag-input-wrapper">
              {isMobile ? (
                <div 
                  className="mobile-tag-input-trigger"
                  onClick={handleMobileTagClick}
                  onTouchStart={handleMobileTagTouch}
                  onTouchEnd={handleMobileTagTouchEnd}
                  role="button"
                  tabIndex={0}
                  style={{ 
                    cursor: 'pointer', 
                    userSelect: 'none',
                    WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                    WebkitTouchCallout: 'none'
                  }}
                >
                  <span style={{ pointerEvents: 'none' }}>
                    {tags.length === 0 ? "点击添加标签 (最多5个)" : "点击添加更多标签"}
                  </span>
                </div>
              ) : (
                <div className="tag-input-with-icon">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleAddTag}
                    onBlur={handleTagInputBlur}
                    onFocus={handleTagInputFocus}
                    onCompositionStart={() => setIsComposing(true)}
                    onCompositionEnd={() => setIsComposing(false)}
                    placeholder={tags.length === 0 ? "添加标签 (最多5个)" : ""}
                    className="tag-input"
                    maxLength={20}
                  />
                  {tagInput.length > 0 && (
                    isLoadingTags ? (
                      <SpinnerIcon size={14} className="fa-spin" title="搜索中" />
                    ) : (
                      <SearchIcon size={14} title="搜索标签" />
                    )
                  )}
                </div>
              )}
              {!isMobile && showSuggestions && (
                <div className="tag-suggestions">
                  {isLoadingTags ? (
                    <div className="tag-suggestion-item loading">
                      <SpinnerIcon size={14} className="fa-spin" /> 加载中...
                    </div>
                  ) : (
                    suggestedTags.length > 0 ? (
                      suggestedTags.map(tag => (
                        <div 
                          key={tag} 
                          className="tag-suggestion-item"
                          onClick={() => handleSelectSuggestedTag(tag)}
                        >
                          {tag}
                        </div>
                      ))
                    ) : (
                      <div className="tag-suggestion-item no-results">
                        未找到标签，按回车创建新标签
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 调试信息 - 仅在开发环境显示 */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '8px',
          borderRadius: '4px',
          fontSize: '12px',
          zIndex: 9999
        }}>
          <div>isMobile: {isMobile ? 'true' : 'false'}</div>
          <div>Width: {window.innerWidth}</div>
          <div>UserAgent: {navigator.userAgent.includes('iPhone') ? 'iPhone' : 'Other'}</div>
          <div>Touch: {'ontouchstart' in window ? 'Yes' : 'No'}</div>
        </div>
      )}

      {/* Mobile Tag Input Modal */}
      {showMobileTagModal && (
        <div className="mobile-tag-modal-overlay" onClick={handleMobileTagModalClose}>
          <div className="mobile-tag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-tag-modal-header">
              <h3>添加标签</h3>
              <button 
                type="button" 
                className="mobile-tag-modal-close"
                onClick={handleMobileTagModalClose}
              >
                <CloseIcon size={16} />
              </button>
            </div>
            
            <div className="mobile-tag-modal-content">
              <div className="mobile-tag-current-tags">
                {tags.map(tag => (
                  <span key={tag} className="tag">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)}>
                      <CloseIcon size={12} />
                    </button>
                  </span>
                ))}
              </div>
              
              <div className="mobile-tag-input-section">
                <input
                  type="text"
                  value={tagInput}
                  onChange={handleMobileTagInputChange}
                  onKeyDown={handleMobileTagAdd}
                  onCompositionStart={() => setIsComposing(true)}
                  onCompositionEnd={() => setIsComposing(false)}
                  placeholder="输入标签名称"
                  className="mobile-tag-input"
                  maxLength={20}
                  autoFocus
                />
                <button 
                  type="button"
                  className="mobile-tag-add-button"
                  onClick={addCurrentTag}
                  disabled={!tagInput.trim() || tags.includes(tagInput.trim())}
                >
                  添加
                </button>
              </div>
              
              {showSuggestions && suggestedTags.length > 0 && (
                <div className="mobile-tag-suggestions">
                  <div className="mobile-tag-suggestions-title">建议标签</div>
                  <div className="mobile-tag-suggestions-list">
                    {suggestedTags.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        className="mobile-tag-suggestion-item"
                        onClick={() => handleSelectSuggestedTag(tag)}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IdeaForm;